import { styled, keyframes } from '@mui/material/styles'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import Box from '@/components/common/Box'
import { colors, spacing, typography } from '@/styles/designTokens'

// Slide in from below; slide out downward
const slideEnter = keyframes`
  from {
    max-height: 0;
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    max-height: 56px;
    opacity: 1;
    transform: translateY(0);
  }
`

const slideLeave = keyframes`
  from {
    max-height: 56px;
    opacity: 1;
    transform: translateY(0);
  }
  to {
    max-height: 0;
    opacity: 0;
    transform: translateY(12px);
  }
`

// Shake + highlight nudge — triggered when checkout retried while warning is showing
const nudgeShake = keyframes`
  0%, 100% { transform: translateX(0); }
  15%       { transform: translateX(-4px); }
  30%       { transform: translateX(4px); }
  45%       { transform: translateX(-3px); }
  60%       { transform: translateX(3px); }
  75%       { transform: translateX(-1px); }
  90%       { transform: translateX(1px); }
`

const nudgeHighlight = keyframes`
  0%, 100% { background-color: ${colors.warningYellowLight}; }
  50%       { background-color: ${colors.warningPulseStart}; }
`

const reducedMotionHighlight = keyframes`
  0%, 100% { background-color: ${colors.warningYellowLight}; }
  50%       { background-color: ${colors.warningPulseEnd}; }
`

interface AlertShellProps {
	isVisible: boolean
}

export const AlertShell = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isVisible',
})<AlertShellProps>(({ isVisible }) => ({
	overflow: 'hidden',
	flexShrink: 0,
	width: '100%',
	animation: isVisible
		? `${slideEnter} 0.32s cubic-bezier(0.22, 1, 0.36, 1) both`
		: `${slideLeave} 0.24s cubic-bezier(0.4, 0, 1, 1) both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		maxHeight: isVisible ? 56 : 0,
		opacity: isVisible ? 1 : 0,
		transform: 'none',
	},
}))

type AlertVariant = 'below-min' | 'min-reached' | 'above-max'

interface AlertContentProps {
	alertVariant: AlertVariant
	isNudging: boolean
}

const bgByVariant: Record<AlertVariant, string> = {
	'below-min': colors.warningYellowLight,
	'min-reached': colors.successLight,
	'above-max': colors.errorLight,
}

export const AlertContent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'alertVariant' && prop !== 'isNudging',
})<AlertContentProps>(({ alertVariant, isNudging }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: spacing.sm,
	paddingLeft: spacing.md,
	paddingRight: spacing.md,
	paddingTop: spacing.sm,
	paddingBottom: spacing.sm,
	backgroundColor: bgByVariant[alertVariant],
	width: '100%',
	animation:
		isNudging && alertVariant === 'below-min'
			? `${nudgeShake} 0.62s ease both, ${nudgeHighlight} 0.62s ease both`
			: 'none',
	'@media (prefers-reduced-motion: reduce)': {
		animation:
			isNudging && alertVariant === 'below-min'
				? `${reducedMotionHighlight} 0.35s ease both`
				: 'none',
	},
}))

// warning.main (#ED6C02) passes 3:1 non-text contrast against warningYellowLight background (WCAG 1.4.11).
// warningFigma (#F6A735) fails at ~1.84:1 on the same background.
export const BelowMinIcon = styled(ErrorRoundedIcon)({
	fontSize: typography.fontSizeIconLg,
	flexShrink: 0,
	color: colors.warningFigma,
})

export const AlertText = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	flex: '1 0 0',
	minWidth: 0,
	paddingTop: spacing.xxs,
	paddingBottom: spacing.xxs,
})
